/**
 * P_ANALYTICS_TIMESTAMPS_1.0 — Reusable timestamp cell.
 *
 * Renders a local-zoned timestamp string with seconds precision and
 * exposes the full ISO 8601 timestamp (with millisecond precision) via
 * a native tooltip. Also copies the raw ISO to the clipboard on click,
 * so operators investigating potential duplicate tap logging can
 * distinguish `21:15:02.123` from `21:15:02.456`.
 *
 * Usage:
 *     <Timestamp value={row.timestamp} />                // default: fmtLocalShort
 *     <Timestamp value={row.timestamp} variant="long" /> // fmtLocalDateTime
 *     <Timestamp value={row.timestamp} variant="time" /> // hh:mm:ss only
 */
import { fmtLocalShort, fmtLocalDateTime, fmtLocalTime, fmtISOWithMillis } from '../../utils/localTime';


const format = (value, variant) => {
    if (variant === 'long') return fmtLocalDateTime(value);
    if (variant === 'time') return fmtLocalTime(value);
    return fmtLocalShort(value);
};


export const Timestamp = ({
    value,
    variant = 'short',
    className = '',
    testid,
    fallback = '—',
}) => {
    if (!value) {
        return <span className={className}>{fallback}</span>;
    }
    const shown = format(value, variant);
    const iso = fmtISOWithMillis(value);
    if (!shown) return <span className={className}>{fallback}</span>;

    const onCopy = async (e) => {
        // Ctrl/Cmd-click to copy the full ISO to clipboard for pasting
        // into audit tickets or DB queries.
        if (!(e.ctrlKey || e.metaKey) || !iso) return;
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(iso);
        } catch {
            /* clipboard unavailable — silent, non-critical */
        }
    };

    return (
        <time
            dateTime={iso || undefined}
            title={iso || ''}
            onClick={onCopy}
            className={`font-mono tabular-nums cursor-help ${className}`}
            data-testid={testid}
            data-iso={iso}
        >
            {shown}
        </time>
    );
};

export default Timestamp;
